import { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
} from '@mui/material';

interface MergeDialogProps<T> {
  open: boolean;
  onClose: () => void;
  onMerge: (target: T) => void;
  sourceItem: T | null;
  searchOptions: T[];
  onSearch: (query: string) => void;
  isLoading: boolean;
  isSearchLoading: boolean;
  getOptionLabel: (option: T) => string;
  entityName: string; // e.g., "препарат", "продукт"
}

export function MergeDialog<T extends { name: string }>({
  open,
  onClose,
  onMerge,
  sourceItem,
  searchOptions,
  onSearch,
  isLoading,
  isSearchLoading,
  getOptionLabel,
  entityName,
}: MergeDialogProps<T>) {
  const [targetItem, setTargetItem] = useState<T | null>(null);

  const handleMergeClick = () => {
    if (targetItem) {
      onMerge(targetItem);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Об'єднати {entityName}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Ви збираєтеся об'єднати {entityName} <strong>{sourceItem?.name}</strong> з системним записом.
          Усі записи в календарі будуть оновлені, а цей запис буде видалено.
        </Typography>
        <Autocomplete
          options={searchOptions}
          getOptionLabel={getOptionLabel}
          loading={isSearchLoading}
          value={targetItem}
          onChange={(_event, newValue) => setTargetItem(newValue)}
          onInputChange={(_event, newInputValue) => onSearch(newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Пошук системного ${entityName}а`}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button
          onClick={handleMergeClick}
          disabled={!targetItem || isLoading}
          variant="contained"
        >
          {isLoading ? <CircularProgress size={24} /> : "Об'єднати"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
